import { Input, Form } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';
import { FC, useContext } from 'react';
import { OfferTypeUnit } from 'types/tradePromoOptimization';
import dayjs, { Dayjs } from 'dayjs';
import { FormInstance } from 'antd/es/form';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

type Props = {
    form: FormInstance<any>;
    disabledEvent: boolean;
};

const EventInfo: FC<Props> = ({ form, disabledEvent }) => {
    //context
    const { appConfig } = useContext(UserContext);

    const { selectedPlanInfo } = useContext(TPODataContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const dateFormat = commonConfig?.dateFormat;
    const planYear = selectedPlanInfo?.planYear;

    //check if start date is less than end date
    const validateStartDate = (_: any, value: Dayjs) => {
        const endDate = form.getFieldValue('CRAEndDate') as Dayjs;
        if (!value || !endDate || value.isBefore(endDate)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Start date must be earlier than end date.'));
    };

    //check if end date is greater than start date
    const validateEndDate = (_: any, value: Dayjs) => {
        const startDate = form.getFieldValue('CRAStartDate') as Dayjs;
        if (!value || !startDate || value.isAfter(startDate)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('End date must be later than start date.'));
    };

    return (
        <div className="w-full  flex flex-col flex-grow gap-3 border border-backgroundGreyAlt6 rounded p-3 bg-white ">
            <div className="flex flex-col h-full gap-6 bg-backgroundGreyAlt6 p-3 pr-4 rounded overflow-y-scroll">
                <Form
                    disabled={disabledEvent}
                    form={form}
                    name="event"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                >
                    <Form.Item
                        label="Event Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter Event Name' }]}
                    >
                        <Input className="font-xs" type="text" style={{ borderRadius: 6 }} placeholder="Event Name" />
                    </Form.Item>

                    <Form.Item
                        dependencies={['CRAEndDate']}
                        label="Event Start Date"
                        name="CRAStartDate"
                        rules={[
                            // { required: true, message: 'Please enter CRA Start Date' },
                            { validator: validateStartDate },
                        ]}
                    >
                        <DatePicker
                            allowClear={false}
                            disabledDate={(date) => {
                                const yearCondition = date.year() !== planYear; //disable all dates other than plan year
                                const dayCondition = date.isBefore(dayjs(), 'day'); //disable dates before current date
                                return yearCondition || dayCondition;
                            }}
                            disabled
                            defaultPickerValue={dayjs(`${planYear}-${dayjs().format('MM')}-01`)}
                            format={dateFormat}
                            style={{ width: '100%', borderRadius: 6 }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Event End Date"
                        name="CRAEndDate"
                        rules={[
                            { validator: validateEndDate },
                            // { required: true, message: 'Please enter CRA End Date' },
                        ]}
                    >
                        <DatePicker
                            disabled
                            disabledDate={(date) => {
                                //    const yearCondition = date.year() !== planYear; //disable all dates other than plan year
                                const dayBeforeCondition = date.isBefore(dayjs(), 'day'); //disable dates before current date
                                const dayAfterCondition = date.isAfter(dayjs(`${(planYear ?? 0) + 1}-01-31`)); //allow end dates to go till jan end of nest year
                                return dayAfterCondition || dayBeforeCondition;
                            }}
                            defaultPickerValue={dayjs(`${planYear}-${dayjs().format('MM')}-01`)}
                            allowClear={false}
                            format={dateFormat}
                            style={{ width: '100%', borderRadius: 6 }}
                        />
                    </Form.Item>
                    {/* hiding below controls and defaulting it to 80 for api call  */}
                    {/* <Form.Item
                        label="Display ACV (%)"
                        name="displayACV"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.resolve();
                                    }
                                    const numValue = Number(value);
                                    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                                        return Promise.reject(new Error('Value must be between 0 and 100'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input
                            className="font-xs"
                            type="number"
                            style={{ borderRadius: 6 }}
                            placeholder="Display ACV"
                            min={0}
                            max={100}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Feature ACV (%)"
                        name="featureACV"
                        //  rules={[{ required: true, message: 'Please enter Feature ACV' }]}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.resolve();
                                    }
                                    const numValue = Number(value);
                                    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                                        return Promise.reject(new Error('Value must be between 0 and 100'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input
                            className="font-xs"
                            type="number"
                            style={{ borderRadius: 6 }}
                            placeholder="Feature ACV"
                            min={0}
                            max={100}
                        />
                    </Form.Item> */}
                </Form>
            </div>
        </div>
    );
};

export default EventInfo;

const AbsPercentageSuffix = ({
    form,
    offerTypeUnitValue,
    disabled,
}: {
    form: FormInstance<any>;
    offerTypeUnitValue: OfferTypeUnit;
    disabled: boolean;
}) => {
    return (
        <>
            <span
                onClick={() => {
                    if (disabled) return;
                    form.setFieldValue('offerTypeUnit', 'abs');
                }}
                className={`mr-2 ${offerTypeUnitValue === 'abs' ? 'text-purpleFont font-bold' : 'text-promoColour'}`}
            >
                Abs
            </span>
            <span
                onClick={() => {
                    if (disabled) return;
                    form.setFieldValue('offerTypeUnit', '%');
                }}
                className={`${offerTypeUnitValue === '%' ? 'text-purpleFont font-bold' : 'text-promoColour'}`}
            >
                %
            </span>
        </>
    );
};
